import AffiliateSelect from '@/components/Form/Shared/AffiliateSelect'
import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import ContactIcons from '@/icons/ContactIcons'
import {
    CONTACT_ROLE_OPTIONS,
    CUSTOMER_SOURCES,
    INDUSTRY_OPTIONS,
} from '@/modules/Customer/constants'
import { CustomerContact } from '@/modules/Customer/types'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import React from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GridHookForm, Typogaphy } from 'vnetwork-ui'

interface ContactEditFormProps {
    editFormControl: any
    onSave: (values: CustomerContact) => Promise<void>
    isPending: boolean
    onCancel: () => void
}

const ContactEditForm: React.FC<ContactEditFormProps> = ({
    editFormControl,
    onSave,
    isPending,
    onCancel,
}) => {
    const { t } = useTranslation()

    return (
        <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <ContactIcons />
                    <Typogaphy className="font-medium">
                        {t('contact-information')}
                    </Typogaphy>
                </div>
            </div>

            <GridHookForm
                formControl={editFormControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onCancel,
                    },
                    {
                        title: t('save'),
                        type: 'submit',
                        variant: 'contained',
                        disabled: isPending,
                        loading: isPending,
                    },
                ]}
                onSubmit={editFormControl.handleSubmit(onSave)}
                disabled={isPending}
                data={[
                    {
                        formItemProps: {
                            name: 'contact_name',
                            type: 'textfield',
                            label: t('contact_name'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_email',
                            type: 'textfield',
                            label: 'Email',
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_phone',
                            type: 'textfield',
                            label: t('employee_phone_number'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_position',
                            type: 'textfield',
                            label: t('position_id'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_industry',
                            type: 'select',
                            label: t('customer_industry'),
                            options: INDUSTRY_OPTIONS,
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_source',
                            type: 'select',
                            label: t('customer_source'),
                            options: CUSTOMER_SOURCES(t)?.filter(
                                (item) => item.value !== 'marketing_approach'
                            ),
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_roles',
                            type: 'select',
                            label: t('contact_roles.label', {
                                ns: 'customer',
                            }),
                            options: CONTACT_ROLE_OPTIONS(t),
                            multiple: true,
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_language',
                            type: 'select',
                            label: t('customer_language'),
                            options: [
                                { label: 'Tiếng Việt (vi)', value: 'vi' },
                                { label: 'English (en)', value: 'en' },
                                { label: '日本語 (ja)', value: 'ja' },
                                { label: '한국어 (ko)', value: 'ko' },
                                { label: '中文 (zh)', value: 'zh' },
                            ],
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: () => (
                            <Controller
                                name="contact_service_interestings"
                                control={editFormControl.control}
                                render={({ field }) => (
                                    <AsyncSelect
                                        control={editFormControl.control}
                                        {...field}
                                        asyncOptions={getProductsGroupOptions}
                                        multiple
                                        allowClear={false}
                                        label={t(
                                            'customer_service_interestings'
                                        )}
                                    />
                                )}
                            />
                        ),
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: () => (
                            <EmployeeSelect
                                control={editFormControl.control}
                                name="contact_cs_followers"
                                multiple
                                label={t('customer_cs_followers')}
                            />
                        ),
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: () => (
                            <EmployeeSelect
                                control={editFormControl.control}
                                name="contact_sale_followers"
                                multiple
                                label={t('customer_sales_followers')}
                            />
                        ),
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: () => (
                            <AffiliateSelect
                                control={editFormControl.control}
                                name="affiliate_id"
                                label={t('affiliate_of_customer', {
                                    ns: 'customer',
                                })}
                            />
                        ),
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'customer_website',
                            type: 'textfield',
                            label: t('website'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_address',
                            type: 'textfield',
                            label: t('address'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_note',
                            type: 'textarea',
                            label: t('note'),
                        },
                        className: 'col-span-4',
                    },
                ]}
                className="grid grid-cols-1 md:grid-cols-4 gap-4"
            />
        </div>
    )
}

export default ContactEditForm
